import { mapActions } from 'vuex'
import Question from '@/components/Exercise/Question'
import Choices from '@/components/Exercise/Choices'
import NextButton from '@/components/Exercise/NextButton'
import PrevButton from '@/components/Exercise/PrevButton'
import Stepper from '@/components/Exercise/Stepper'

export default (config = {}) => {
  const exerciseType = config.type
  const useAudio = config.useAudio || false
  const maxQuestion = config.maxQuestion || 10

  return {
    components: {
      Question,
      Choices,
      NextButton,
      PrevButton,
      Stepper,
    },
    props: {
      aside: {
        type: Number,
      },
      history: {
        type: Object,
        required: true,
      },
      questions: {
        type: Array,
      },
      readOnly: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        selects: [],
        answered: false,
        showAnswer: false,
        isLoading: false,
        resultInterval: null,
        limitInterval: null,

        readStep: 1,
        resultStep: 0,
      }
    },
    created () {
      this.$on('result:start', () => this.showAnswer = true)
      this.$on('result:end', () => this.showAnswer = false)
    },
    computed: {
      choices () {
        const question = this.question || {}
        return question.choices || []
      },
      question () {
        return this.readOnly
          ? this.questions[this.questions.length - this.readStep]
          : this.questions[0]
      },
      canProceed () {
        return this.selects.length > 0 || this.resultStep > 0
      },
      disableNextButton () {
        return this.resultStep > 0 && this.isLoading
      },
      canSelect () {
        return !this.showAnswer && !this.isLoading
      },
      correctChoices () {
        return this.choices.filter(choice => this.answerIsCorrectGroup(choice))
      },
      maxQuestion () {
        return maxQuestion
      },
      withTimeLimited () {
        return this.history.time_limited
      },
      currentStep () {
        return this.readOnly
          ? this.readStep
          : this.questions.length
      },
      canReadNext () {
        return this.readStep < this.questions.length
      },
      canReadPrev () {
        return this.readStep > 1
      },
      readSelects () {
        const qa = this.question.answers || []
        const qc = this.choices
        return this.choices.reduce((carry, c, i) => {
          const has = qa.find(a => a.choice_id == c.id)
          if (has) {
            return [ ...carry, i ]
          }
          return carry
        }, [])
      },
      timeLimit () {
        return 3000
      },
    },
    methods: {
      async init () {
        try {
          await this.initQuestion()
          if (useAudio) {
            await this.initAudio()
            if (!this.readOnly) {
              this.audioPlay()
            }
          }
          if (!this.readOnly && this.withTimeLimited) {
            this.$on('timelimit:end', this.submitAnswer)
            if (useAudio && this.hasAudio) {
              this.$once('audio:autoend', this.startTimeLimit)
            } else {
              this.startTimeLimit()
            }
          }
        } catch (e) {
          //
        }
      },
      async initQuestion () {
        try {
          let questions = await this.getPreviousQuestions()
          if (!this.readOnly) {
            const question = await this.createQuestion()
            questions = [question, ...questions ]
          }
          this.handleUpdateQuestion([ ...questions ])
        } catch (e) {
          //
        }
      },
      handleUpdateQuestion (questions) {
        this.$emit('update:questions', questions)
      },
      async getPreviousQuestions () {
        try {
          const { data } = await this.httpGet({
            url: '/question',
            query: { history_id: this.history.id },
          })
          return data
        } catch (e) {
          //
        }
      },
      async createQuestion () {
        try {
          const { data } = await this.httpPost({
            url: '/question',
            data: {
              history_id: this.history.id,
            },
          })
          return data
        } catch (e) {
          //
        }
      },
      async submitAnswer () {
        switch (this.resultStep) {
          case 0:
            this.resultStep++
            await this.postAnswer()
            break
          case 1:
            this.resultStep++
            await this.resultProceed()
            this.resultStep = 0
            break
        }
      },
      async postAnswer () {
        this.isLoading = true
        this.$emit('result:start')
        try {
          if (useAudio) {
            this.audioStop()
          }
          if (this.withTimeLimited) {
            this.stopTimeLimit()
            if (useAudio) {
              this.$off('audio:autoend', this.startTimeLimit)
            }
          }
          await this.createAnswer()
        } catch (e) {
          //
        } finally {
          this.isLoading = false
        }
      },
      async resultProceed () {
        this.isLoading = true
        try {
          const proceed = await this.exerciseProceed()
          if (!proceed) {
            return
          }

          if (useAudio) {
            this.audioUnload()
            await this.initAudio()
            if (!this.readOnly) {
              this.audioPlay()
            }
          }
          if (this.withTimeLimited) {
            if (useAudio && this.hasAudio) {
              this.$once('audio:autoend', this.startTimeLimit)
            } else {
              this.startTimeLimit()
            }
          }
        } catch (e) {
          //
        } finally {
          this.$emit('result:end')
          this.isLoading = false
        }
      },
      async exerciseProceed () {
        this.selects = []

        if (this.questions.length >= maxQuestion) {
          this.$emit('end')
          return false
        }

        try {
          const question = await this.createQuestion()
          this.handleUpdateQuestion([ question, ...this.questions ])
          return true
        } catch (e) {
          //
        }
      },
      async createAnswer () {
        try {
          const payload = {
            question_id: this.question.id,
            answers: this.selects.map(n => {
              return {
                choice_id: this.choices[n].id,
              }
            }),
          }
          const { data } = await this.httpPost({
            url: '/answer',
            data: payload,
          })
          return data
        } catch (e) {
          //
        }
      },
      isCorrectAnswer (n) {
        return this.answerIsCorrectGroup(this.choices[n])
      },
      answerIsCorrectGroup (choice) {
        const groupId = this.question.group_id
        return choice.word.group_id == groupId
      },
      startResultCountdown () {
        return new Promise((resolve) => {
          this.$emit('result:start')
          this.resultInterval = setTimeout(() => {
            resolve()
            this.$emit('result:end')
            this.resultInterval = null
          }, 2000)
        })
      },
      stopResultCountdown () {
        clearTimeout(this.resultInterval)
        this.resultInterval = null
      },
      startTimeLimit () {
        return new Promise((resolve) => {
          this.$emit('timelimit:start')
          this.limitInterval = setTimeout(() => {
            resolve()
            this.$emit('timelimit:end')
            this.limitInterval = null
          }, this.timeLimit)
        })
      },
      stopTimeLimit () {
        clearTimeout(this.limitInterval)
        this.limitInterval = null
      },
      isSelected (n) {
        const selects = this.readOnly
          ? this.readSelects
          : this.selects
        return selects.includes(n)
      },
      toggleSelect (n) {
        const has = this.selects.includes(n)
        if (has) {
          this.selects = this.selects.filter(s => s !== n)
        } else {
          this.selects = [ ...this.selects, n ]
        }
      },
      reSelect (n) {
        this.selects = [n]
      },
      introduced (val = true) {
        this.isIntroduced = val
      },
      async readNextStep () {
        this.readStep = this.readStep + 1
        if (useAudio) {
          this.audioUnload()
          await this.initAudio()
        }
      },
      async readPrevStep () {
        this.readStep = this.readStep - 1
        if (useAudio) {
          this.audioUnload()
          await this.initAudio()
        }
      },
      ...mapActions('http', {
        httpGet: 'get',
        httpPost: 'post',
      }),
    },
  }
}
